import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import clientMinorService from '../services/clientMinorService';

const PAGE_SIZE = 20;
const normalize = (s) =>
  (s ?? '')
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export default function TiKaneMinorModal({ show, onHide, onSave }) {
  const {
    control, register, handleSubmit, watch, reset, formState: { errors }
  } = useForm({
    defaultValues: {
      minorId: null,
      startDate: '',
      durationDays: '',
      menCount: '',
      feePercent: 5,
      feeFixedGourde: 0,
      status: 'OPEN'
    }
  });

  // calculs
  const duration = parseFloat(watch('durationDays'))   || 0;
  const men      = parseFloat(watch('menCount'))       || 0;
  const feeP     = Math.max(0, Math.min(100, parseFloat(watch('feePercent')) || 0));
  const feeFix   = Math.max(0, parseFloat(watch('feeFixedGourde')) || 0);
  const daily    = men * 100;
  const total    = daily * duration;
  const percentFeeAmount = total * (feeP / 100);
  const finalRaw = total - percentFeeAmount - feeFix;
  const finalv   = Math.max(0, finalRaw);

  // dataset pour afficher l’option sélectionnée
  const [minors, setMinors] = useState([]);

  useEffect(() => {
    if (!show) return;
    reset({
      minorId: null,
      startDate: '',
      durationDays: '',
      menCount: '',
      feePercent: 5,
      feeFixedGourde: 0,
      status: 'OPEN'
    });
    clientMinorService.listAll().then(setMinors).catch(()=>setMinors([]));
  }, [show, reset]);

  // option builder
  const toMinorOpt  = (m) => ({ value: m.id, label: `${m.firstName||''} ${m.lastName||''} (mineur)` });

  // valeur visible pour AsyncPaginate (à partir du minorId)
  const selectedMinorOption = useMemo(() => {
    const id = watch('minorId');
    if (!id) return null;
    const m = minors.find(x => x.id === id);
    return m ? toMinorOpt(m) : { value: id, label: `Mineur #${id}` };
  }, [minors, watch('minorId')]);

  // chargement paginé
  const loadMinorOptions = async (inputValue, _loaded, { page }) => {
    const q = normalize(inputValue);
    const filtered = minors.filter(m =>
      normalize(
        `${m.firstName||''} ${m.lastName||''} ${m.parent?.firstName||''} ${m.parent?.lastName||''}`
      ).includes(q)
    );
    const start = (page - 1) * PAGE_SIZE;
    const slice = filtered.slice(start, start + PAGE_SIZE).map(toMinorOpt);
    return { options: slice, hasMore: start + PAGE_SIZE < filtered.length, additional: { page: page+1 } };
  };

  const submit = (vals) => {
    onSave({
      // adulte = null pour un compte mineur (géré côté backend)
      clientId: null,
      minorId: vals.minorId,
      startDate: vals.startDate,
      durationDays: vals.durationDays,
      menCount: vals.menCount,
      status: 'OPEN',
      dailyValue: daily.toFixed(2),
      totalValue: total.toFixed(2),
      finalValue: finalv.toFixed(2),
      feePercent: Number(feeP.toFixed(2)),
      feeFixedGourde: Number(feeFix.toFixed(2))
    });
  };

  return (
    <Modal show={show} onHide={onHide} backdrop="static" size="lg">
      <Modal.Header closeButton><Modal.Title>Ouvrir un compte Ti Kanè (Mineur)</Modal.Title></Modal.Header>
      <Form onSubmit={handleSubmit(submit)}>
        <Modal.Body>
          <Row className="g-3 mb-2">
            <Col md={12}>
              <Form.Label>Mineur *</Form.Label>
              <Controller
                name="minorId"
                control={control}
                rules={{ required: 'Requis' }}
                render={({ field }) => (
                  <AsyncPaginate
                    value={selectedMinorOption}
                    onChange={(opt) => field.onChange(opt?.value ?? null)}
                    loadOptions={loadMinorOptions}
                    additional={{ page: 1 }}
                    debounceTimeout={300}
                    placeholder="Rechercher un mineur…"
                    isClearable
                  />
                )}
              />
              {errors.minorId && <Form.Text className="text-danger">{errors.minorId.message}</Form.Text>}
            </Col>
          </Row>

          <Row className="g-3">
            <Col md>
              <Form.Group controlId="startDate">
                <Form.Label>Date d’ouverture *</Form.Label>
                <Form.Control type="date" {...register('startDate', { required: 'Requis' })} />
                {errors.startDate && <Form.Text className="text-danger">{errors.startDate.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md>
              <Form.Group controlId="durationDays">
                <Form.Label>Durée (jours) *</Form.Label>
                <Form.Control type="number" {...register('durationDays', { required: 'Requis', min: { value: 1, message: '>= 1' } })}/>
                {errors.durationDays && <Form.Text className="text-danger">{errors.durationDays.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md>
              <Form.Group controlId="menCount">
                <Form.Label>Nombre de men *</Form.Label>
                <Form.Control type="number" step="0.01" {...register('menCount', { required: 'Requis', min: { value: 0, message: '>= 0' } })}/>
                {errors.menCount && <Form.Text className="text-danger">{errors.menCount.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md>
              <Form.Group controlId="feePercent">
                <Form.Label>Frais (%) *</Form.Label>
                <Form.Control type="number" step="0.01" {...register('feePercent', { required: 'Requis', min: { value: 0, message: '>= 0' }, max: { value: 100, message: '<= 100' } })}/>
                {errors.feePercent && <Form.Text className="text-danger">{errors.feePercent.message}</Form.Text>}
              </Form.Group>
            </Col>
            <Col md>
              <Form.Group controlId="feeFixedGourde">
                <Form.Label>Frais fixe (HTG)</Form.Label>
                <Form.Control type="number" step="0.01" {...register('feeFixedGourde', { min: { value: 0, message: '>= 0' } })}/>
                {errors.feeFixedGourde && <Form.Text className="text-danger">{errors.feeFixedGourde.message}</Form.Text>}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col><Form.Text>Valeur/jour : {daily.toFixed(2)}</Form.Text></Col>
            <Col><Form.Text>Total cumulé : {total.toFixed(2)}</Form.Text></Col>
            <Col><Form.Text>Frais % : {percentFeeAmount.toFixed(2)}</Form.Text></Col>
            <Col><Form.Text>Frais fixe : {feeFix.toFixed(2)}</Form.Text></Col>
            <Col><Form.Text>Valeur finale : {finalv.toFixed(2)}</Form.Text></Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button type="submit" variant="primary">Ouvrir le compte mineur</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
